import { RefObject, useEffect, useMemo } from 'react';
import { TikTokService } from './TikTok.service';

export function createTikTokActionDispatcher(
  send: (action: 'TYPING' | 'MARK_READ') => Promise<void>,
  now = Date.now,
) {
  let lastTyping = -Infinity;
  const seen = new Set<string>();
  const pending = new Set<string>();
  return {
    typing() {
      if (now() - lastTyping < 5000) return;
      lastTyping = now();
      void send('TYPING').catch(() => {});
    },
    read(messageId: string) {
      if (!messageId || seen.has(messageId) || pending.has(messageId)) return;
      pending.add(messageId);
      void send('MARK_READ')
        .then(() => {
          seen.clear();
          seen.add(messageId);
        })
        .catch(() => {})
        .finally(() => pending.delete(messageId));
    },
  };
}
export function useTikTokActions(
  workspaceId: string | undefined,
  leadId: string | undefined,
  enabled: boolean,
) {
  return useMemo(
    () =>
      createTikTokActionDispatcher((action) =>
        enabled && workspaceId && leadId
          ? TikTokService.senderAction(workspaceId, leadId, action)
          : Promise.resolve(),
      ),
    [workspaceId, leadId, enabled],
  );
}
export function useTikTokReadMarker(args: {
  workspaceId?: string;
  leadId?: string;
  enabled: boolean;
  ready: boolean;
  latestInboundId: string;
  scrollRef: RefObject<HTMLDivElement>;
}) {
  const actions = useTikTokActions(args.workspaceId, args.leadId, args.enabled);
  useEffect(() => {
    if (!args.enabled || !args.ready || !args.latestInboundId) return;
    const check = () => {
      const root = args.scrollRef.current;
      if (
        root &&
        document.visibilityState === 'visible' &&
        document.hasFocus() &&
        root.scrollHeight - root.scrollTop - root.clientHeight <= 24
      )
        actions.read(args.latestInboundId);
    };
    const root = args.scrollRef.current;
    root?.addEventListener('scroll', check);
    window.addEventListener('focus', check);
    document.addEventListener('visibilitychange', check);
    const frame = requestAnimationFrame(check);
    return () => {
      cancelAnimationFrame(frame);
      root?.removeEventListener('scroll', check);
      window.removeEventListener('focus', check);
      document.removeEventListener('visibilitychange', check);
    };
  }, [actions, args.enabled, args.ready, args.latestInboundId, args.scrollRef]);
}
